import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Typography,
    useTheme,
    CircularProgress,
} from "@mui/material";
import { Button } from "@foodpilot/foods";
import { PuckPage } from "src/api/client-api/cms/useCreatePuckPage";
import { normalizeUrl, isUrlValid } from "./urlValidation";

export type DuplicatePageDialogProps = {
    open: boolean;
    sourcePage: PuckPage | null;
    onClose: () => void;
    onConfirm: (payload: { title: string; url: string }) => void;
    isLoading?: boolean;
};

export const DuplicatePageDialog = ({
    open,
    sourcePage,
    onClose,
    onConfirm,
    isLoading = false,
}: DuplicatePageDialogProps) => {
    const { t } = useTranslation();
    const theme = useTheme();

    const defaultTitle = useMemo(() => (sourcePage?.title ? `${sourcePage.title} (copy)` : ""), [sourcePage?.title]);
    const defaultUrl = useMemo(() => {
        if (!sourcePage?.slug) return "";
        const base = sourcePage.slug === "home" ? "home-copy" : `${sourcePage.slug}-copy`;
        return normalizeUrl(`/${base}`);
    }, [sourcePage?.slug]);

    const [title, setTitle] = useState<string>(defaultTitle);
    const [url, setUrl] = useState<string>(defaultUrl);

    useEffect(() => {
        setTitle(defaultTitle);
        setUrl(defaultUrl);
    }, [defaultTitle, defaultUrl]);

    const handleConfirm = () => {
        const normalized = normalizeUrl(
            url ||
                "/" +
                    (title || "")
                        .toLowerCase()
                        .replace(/\s+/g, "-")
                        .replace(/[^a-z0-9\-/]/g, ""),
        );
        if (!title.trim() || !isUrlValid(normalized)) return;
        onConfirm({ title: title.trim(), url: normalized });
    };

    const effectiveUrl = normalizeUrl(
        url ||
            "/" +
                (title || "")
                    .toLowerCase()
                    .replace(/\s+/g, "-")
                    .replace(/[^a-z0-9\-/]/g, ""),
    );
    const urlInvalid = url !== "" && !isUrlValid(url);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Typography variant="body1" fontSize={18} fontWeight={theme.typography.fontWeightMedium}>
                    {t("settings.cms.pages.duplicate_dialog.title", "Duplicate page")}
                </Typography>
            </DialogTitle>
            <DialogContent>
                <TextField
                    margin="dense"
                    id="title"
                    label={t("settings.cms.pages.table.title", "Title")}
                    type="text"
                    fullWidth
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    disabled={isLoading}
                />
                <TextField
                    margin="dense"
                    id="url"
                    label={t("settings.cms.pages.create_dialog.page_url", "URL")}
                    placeholder="/about, /pricing, /custom/path"
                    type="text"
                    fullWidth
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    error={urlInvalid}
                    helperText={
                        urlInvalid ?
                            t("settings.cms.pages.create_dialog.url_invalid", "URL is invalid or reserved")
                        :   ""
                    }
                />
                <Typography variant="caption" color="text.secondary">
                    {t("settings.cms.pages.duplicate_dialog.final_url", "Final URL")}: {effectiveUrl}
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button variant="ternary" onClick={onClose} disabled={isLoading}>
                    {t("settings.cms.pages.dialog.action.cancel", "Cancel")}
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="primary"
                    disabled={isLoading || !title.trim() || !isUrlValid(effectiveUrl)}
                >
                    {isLoading ?
                        <CircularProgress size={24} sx={{ color: "white" }} />
                    :   t("settings.cms.pages.duplicate_dialog.action", "Duplicate")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DuplicatePageDialog;
